"use client"

import { FormikProvider, type FormikErrors, useFormik } from "formik"
import { useState } from "react"

import { FormInput } from "@/components/form-inputs"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { DialogFooter } from "@/components/ui/dialog"
import { useSendMetaAdsTestLeadMutation } from "@/hooks/mutations/meta-ads"
import { normalizeMetaTestPhoneNumber } from "@/lib/meta-ads/phone"
import type { MetaAdsIntegrationView } from "@/lib/meta-ads/types"
import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"


type MetaAdsTestValues = {
  phoneNumber: string
}

export function MetaAdsTestForm({
  onCancel,
  onSuccess,
  cancelLabel = "Cancel",
  footerClassName,
}: {
  onCancel: () => void
  onSuccess: (integration: MetaAdsIntegrationView) => void
  cancelLabel?: string
  footerClassName?: string
}) {
  const tUi = useTranslations("Integrations.IntegrationsMetaAdsMetaAdsTestForm")

  const mutation = useSendMetaAdsTestLeadMutation()
  const [error, setError] = useState<string | null>(null)
  const formik = useFormik<MetaAdsTestValues>({
    initialValues: { phoneNumber: "" },
    validate: validateTestLead,
    onSubmit: async (values, helpers) => {
      setError(null)
      try {
        const integration = await mutation.mutateAsync({
          phoneNumber: normalizeMetaTestPhoneNumber(values.phoneNumber)
            ?? values.phoneNumber.trim(),
          setErrors: errors => helpers.setErrors(errors as FormikErrors<MetaAdsTestValues>),
        })
        onSuccess(integration)
      } catch (submitError: unknown) {
        setError(getErrorMessage(submitError, tUi))
      }
    },
  })

  return (
    <FormikProvider value={formik}>
      <form className="space-y-4" onSubmit={formik.handleSubmit}>
        <Alert>
          <AlertTitle><span key={tUi("useYourOwnPhoneNumber")}>{tUi("useYourOwnPhoneNumber")}</span></AlertTitle>
          <AlertDescription>
            <span key={tUi("thisIsARealEndToEndTest")}>{tUi("thisIsARealEndToEndTest")}</span></AlertDescription>
        </Alert>
        {error ? (
          <Alert variant="destructive" role="alert">
            <AlertTitle><span key={tUi("theTestLeadCouldNotBeSent")}>{tUi("theTestLeadCouldNotBeSent")}</span></AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        <FormInput
          name="phoneNumber"
          type="tel"
          label={tUi("phoneNumberToCall")}
          placeholder="+351…"
          description={tUi("includeTheCountryCodeStartingWithSpacesAre")}
        />
        <DialogFooter className={cn(footerClassName)}>
          <Button type="button" variant="outline" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            <span key={mutation.isPending ? tUi("sendingTest") : tUi("sendTestLead")}>{mutation.isPending ? tUi("sendingTest") : tUi("sendTestLead")}</span>
          </Button>
        </DialogFooter>
      </form>
    </FormikProvider>
  )
}

function validateTestLead(values: MetaAdsTestValues): FormikErrors<MetaAdsTestValues> {
  if (!normalizeMetaTestPhoneNumber(values.phoneNumber)) {
    return { phoneNumber: "Enter a complete phone number with country code, such as +351…" }
  }
  return {}
}

function getErrorMessage(error: unknown, tUi: ReturnType<typeof useTranslations<"Integrations.IntegrationsMetaAdsMetaAdsTestForm">>): string {
  if (error instanceof Error && error.message) return error.message
  return tUi("theMetaTestLeadCouldNotBeSent")
}
